import * as React from 'react';

/**
 * Given an array of media queries and an array with an equal number
 * of values, returns the first value which corresponds to the currently
 * matched media query.
 *
 * IE if the first media query matches, then the first value is returned.
 *
 * @param mediaQueries - an array of Media Queries to match
 * @param matchValues - an array of values to return if the media query matches
 * @param noMatchValue - Value to return if no media queries match
 */
export function useMedia<ValueType>(
  mediaQueries: string[],
  matchValues: ValueType[],
  noMatchValue: ValueType
) {
  const mediaQueryLists = mediaQueries.map((q) => window.matchMedia(q));

  /**
   * Get index of first media query that matches, return related value
   * or defaultValue if none.
   */
  const getValue = () => {
    const index = mediaQueryLists.findIndex((mql) => mql.matches);
    return matchValues?.[index] ?? noMatchValue;
  };

  const [value, setValue] = React.useState<ValueType>(getValue);

  React.useEffect(
    () => {
      const handler = () => setValue(getValue);

      mediaQueryLists.forEach((mql) => {
        if (mql.addEventListener) {
          mql.addEventListener('change', handler);
        } else if (mql.addListener) {
          // Older browsers with deprecated method
          mql.addListener(handler);
        }
      });

      return () =>
        mediaQueryLists.forEach((mql) => {
          if (mql.removeEventListener) {
            mql.removeEventListener('change', handler);
          } else if (mql.removeListener) {
            mql.removeListener(handler);
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return value;
}
